import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Clock, 
  CalendarDays, 
  TrendingUp, 
  Users,
  CheckCircle,
  AlertCircle,
  ClipboardCheck
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, icon: Icon, color = 'primary', subtext }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-emerald-500/10 text-emerald-500',
    warning: 'bg-amber-500/10 text-amber-500',
    danger: 'bg-red-500/10 text-red-500',
  };

  return (
    <div className="stat-card animate-slide-up">
      <div className="flex items-start justify-between">
        <div>
          <p className="stat-label">{label}</p>
          <p className="stat-value">{value}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
        </div>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [teamStats, setTeamStats] = useState(null);
  const [hoursData, setHoursData] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Always fetch personal stats for the connected user
        const personalRes = await axios.get(`${API}/stats/personal`);
        setStats(personalRes.data);

        // Get leave balance for the connected user
        const leaveRes = await axios.get(`${API}/leave-balance/${user.id}`);
        setLeaveBalance(leaveRes.data);

        // Managers/Directors also get team stats
        if (user?.role !== 'collaborateur') {
          const teamRes = await axios.get(`${API}/stats/team`);
          setTeamStats(teamRes.data);

          const now = new Date();
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
          const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
          
          const hoursRes = await axios.get(`${API}/stats/hours-by-user`, {
            params: { start_date: monthStart, end_date: monthEnd }
          });
          setHoursData(hoursRes.data.slice(0, 10));
        }
      } catch (err) {
        toast.error(t('error'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.role, user?.id, t]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-3xl mb-1">{t('dashboard').toUpperCase()}</h1>
        <p className="text-muted-foreground text-sm">
          {t('welcomeBack')}, {user?.first_name} — {t('yourSummary')}
        </p>
      </div>

      {/* Personal Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label={t('hoursThisWeek')}
          value={`${stats?.hours_this_week || 0}${t('h')}`}
          icon={Clock}
        />
        <StatCard
          label={t('hoursThisMonth')}
          value={`${stats?.hours_this_month || 0}${t('h')}`}
          icon={CalendarDays}
        />
        <StatCard
          label={t('pendingAbsences')}
          value={stats?.pending_absences || 0}
          icon={AlertCircle}
          color="warning"
        />
        <StatCard
          label={t('approvedAbsences')}
          value={stats?.approved_absences || 0}
          icon={CheckCircle}
          color="success"
        />
      </div>

      {/* Leave Balance Card */}
      {leaveBalance && leaveBalance.allocated_hours > 0 && (
        <div className="border border-border rounded-lg p-4 bg-card">
          <h3 className="text-lg mb-4">{t('leaveBalance').toUpperCase()} {new Date().getFullYear()}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase">{t('hoursAllocated')}</p>
              <p className="text-2xl font-bold">{leaveBalance.allocated_hours}{t('h')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">{t('hoursUsed')}</p>
              <p className="text-2xl font-bold text-emerald-600">{leaveBalance.used_hours}{t('h')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">{t('hoursPlanned')}</p>
              <p className="text-2xl font-bold text-amber-600">{leaveBalance.planned_hours}{t('h')}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase">{t('hoursRemaining')}</p>
              <p className={`text-2xl font-bold ${leaveBalance.remaining_hours < 0 ? 'text-red-500' : 'text-primary'}`}>
                {leaveBalance.remaining_hours}{t('h')}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <div className="h-3 bg-muted rounded-full overflow-hidden flex">
              <div 
                className="bg-emerald-500 h-full"
                style={{ width: `${(leaveBalance.used_hours / leaveBalance.allocated_hours) * 100}%` }}
              />
              <div 
                className="bg-amber-500 h-full"
                style={{ width: `${(leaveBalance.planned_hours / leaveBalance.allocated_hours) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Manager/Director Stats */}
      {user?.role !== 'collaborateur' && teamStats && (
        <>
          <div className="border-t border-border pt-6">
            <h2 className="text-xl mb-4">{t('teamStats').toUpperCase()}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <StatCard
                label={t('teamMembers')}
                value={teamStats.team_count}
                icon={Users}
              />
              <StatCard
                label={t('totalHoursMonth')}
                value={`${teamStats.total_hours_month}${t('h')}`}
                icon={Clock}
              />
              <StatCard
                label={t('avgPerPerson')}
                value={`${teamStats.avg_hours_per_person}${t('h')}`}
                icon={TrendingUp}
              />
              <StatCard
                label={t('pendingAbsences')}
                value={teamStats.pending_absences}
                icon={AlertCircle}
                color="warning"
              />
              <StatCard
                label={t('pendingCorrections')}
                value={teamStats.pending_corrections || 0}
                icon={ClipboardCheck}
                color="warning"
              />
            </div>
          </div>

          {/* Hours Chart */}
          {hoursData.length > 0 && (
            <div className="border border-border rounded-lg p-4 bg-card">
              <h3 className="text-lg mb-4">{t('hoursPerCollaborator').toUpperCase()}</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hoursData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <YAxis 
                      type="category" 
                      dataKey="user_name" 
                      width={100}
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 'var(--radius)'
                      }}
                      formatter={(value) => [`${value}${t('h')}`, t('hours')]}
                    />
                    <Bar dataKey="hours" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}

      {/* Quick Actions */}
      <div className="border border-border rounded-lg p-4 bg-card">
        <h3 className="text-lg mb-4">{t('quickActions').toUpperCase()}</h3>
        <div className="flex flex-wrap gap-3">
          <a 
            href="/schedule" 
            className="btn-vitalis inline-flex items-center gap-2"
            data-testid="quick-action-schedule"
          >
            <CalendarDays className="h-4 w-4" />
            {t('viewSchedule')}
          </a>
          <a 
            href="/absences" 
            className="btn-vitalis inline-flex items-center gap-2"
            data-testid="quick-action-absences"
          >
            <Clock className="h-4 w-4" />
            {user?.role === 'collaborateur' ? t('requestAbsence') : t('manageAbsences')}
          </a>
          {user?.role !== 'collaborateur' && (
            <a 
              href="/corrections" 
              className="btn-vitalis inline-flex items-center gap-2"
              data-testid="quick-action-corrections"
            >
              <ClipboardCheck className="h-4 w-4" />
              {t('pendingCorrections')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
